import { useCallback, useMemo, useRef, useState } from "react";

import { INIT_ACCOUNT, INIT_DEPARTMENT } from "constants/filter";
import useUpdateFilterConditions from "hooks/api/useUpdateFilterConditions";
import { useLayoutSyncEffect } from "hooks/effects";
import useUserContext from "hooks/useUserContext";
import { filterAccountsByDepartment } from "utils/filterChoiceUtils";
import {
  areFilterConditionsEqual,
  buildFilterConditions,
  clearGlobalFilterLocalStorage,
  hasPendingGlobalFilterSync,
  hasSavedGlobalFilter,
  hasServerGlobalFilter,
  markPendingGlobalFilterSync,
  readSavedDepartment,
  resolveGlobalFilterState,
  resolveSavedAccount,
  saveGlobalFilterToLocalStorage,
} from "utils/globalFilterConditionsUtils";
import { decideHydrationAction } from "utils/globalFilterHydration";

export { hasSavedGlobalFilter } from "utils/globalFilterConditionsUtils";

const isSameDepartment = (left: string | undefined, right: string | undefined): boolean =>
  (left ?? undefined) === (right ?? undefined);

const isSameAccount = (left: Account | undefined, right: Account | undefined): boolean =>
  (left?.id ?? undefined) === (right?.id ?? undefined);

/**
 * 部署・担当者フィルターの global 永続化と部署→担当者連動を管理する
 */
export const useGlobalDepartmentAccountFilter = (accountList: Account[]) => {
  const { user, setUser, isUserLoaded } = useUserContext();
  const { updateFilterConditions } = useUpdateFilterConditions();
  const [chosenDepartment, setChosenDepartmentState] = useState<string | undefined>(undefined);
  const [chosenAccount, setChosenAccountState] = useState<Account | undefined>(undefined);
  const [isFilterReady, setIsFilterReady] = useState(false);
  const hasHydratedRef = useRef(false);
  const hasAttemptedMigrationRef = useRef(false);
  const chosenDepartmentRef = useRef<string | undefined>(undefined);
  const chosenAccountRef = useRef<Account | undefined>(undefined);
  const lastAppliedFilterConditionsKeyRef = useRef<string>("");

  const accountListKey = useMemo(() => accountList.map((account) => account.id).join(","), [accountList]);
  const filterConditionsKey = useMemo(() => JSON.stringify(user.filterConditions ?? {}), [user.filterConditions]);

  const applyHydratedFilter = useCallback((department: string | undefined, account: Account | undefined) => {
    if (!isSameDepartment(chosenDepartmentRef.current, department)) {
      chosenDepartmentRef.current = department;
      setChosenDepartmentState(department);
    }
    if (!isSameAccount(chosenAccountRef.current, account)) {
      chosenAccountRef.current = account;
      setChosenAccountState(account);
    }
  }, []);

  const syncToServer = useCallback(
    async (department: string | undefined, account: Account | undefined) => {
      const filterConditions = buildFilterConditions(department, account);

      if (areFilterConditionsEqual(filterConditions, user.filterConditions)) {
        clearGlobalFilterLocalStorage();
        return;
      }

      try {
        await updateFilterConditions(filterConditions);
        clearGlobalFilterLocalStorage();
        lastAppliedFilterConditionsKeyRef.current = JSON.stringify(filterConditions);
        setUser((previous) => ({
          ...previous,
          filterConditions,
        }));
      } catch {
        saveGlobalFilterToLocalStorage(department, account);
        markPendingGlobalFilterSync();
      }
    },
    [setUser, updateFilterConditions, user.filterConditions]
  );

  useLayoutSyncEffect(() => {
    if (!isUserLoaded || !user.contactId || accountList.length === 0) {
      setIsFilterReady(false);
      return;
    }

    const hydrateFromServer = () => {
      const { department, account } = resolveGlobalFilterState(accountList, user.filterConditions);
      applyHydratedFilter(department, account);
    };

    const hydrateFromLocalStorage = () => {
      const savedDepartment = readSavedDepartment();
      const savedAccount = resolveSavedAccount(accountList, savedDepartment);
      applyHydratedFilter(savedDepartment, savedAccount);
    };

    const migrateFromLocalStorage = async () => {
      if (hasAttemptedMigrationRef.current) {
        return;
      }
      hasAttemptedMigrationRef.current = true;

      const savedDepartment = readSavedDepartment();
      const savedAccount = resolveSavedAccount(accountList, savedDepartment);
      await syncToServer(savedDepartment, savedAccount);
      const { department, account } = resolveGlobalFilterState(accountList, user.filterConditions);
      applyHydratedFilter(department, account);
    };

    const retryPendingSync = async () => {
      if (!hasPendingGlobalFilterSync() && !hasSavedGlobalFilter()) {
        return;
      }

      const savedDepartment = readSavedDepartment();
      const savedAccount = resolveSavedAccount(accountList, savedDepartment);
      await syncToServer(savedDepartment, savedAccount);
      const { department, account } = resolveGlobalFilterState(accountList, user.filterConditions);
      applyHydratedFilter(department, account);
    };

    // 決定されたアクションを、元の呼び出し順序のまま実行する
    const runHydrationAction = (action: ReturnType<typeof decideHydrationAction>) => {
      switch (action) {
        case "hydrateFromServer":
          hydrateFromServer();
          return;
        case "migrateFromLocalStorage":
          void migrateFromLocalStorage();
          hydrateFromLocalStorage();
          return;
        case "retryPendingSync":
          hydrateFromLocalStorage();
          void retryPendingSync();
          return;
        case "hydrateFromLocalStorage":
          hydrateFromLocalStorage();
          return;
        case "none":
        default:
      }
    };

    const alreadyHydrated = hasHydratedRef.current;
    const filterConditionsChanged = lastAppliedFilterConditionsKeyRef.current !== filterConditionsKey;

    // どの hydration を行うかは副作用のない純粋関数で判断する（判断と実行の分離）
    const action = decideHydrationAction({
      alreadyHydrated,
      filterConditionsChanged,
      hasServerFilter: hasServerGlobalFilter(user.filterConditions),
      hasSavedFilter: hasSavedGlobalFilter(),
      hasPendingSync: hasPendingGlobalFilterSync(),
      hasAttemptedMigration: hasAttemptedMigrationRef.current,
    });

    hasHydratedRef.current = true;
    if (!alreadyHydrated || filterConditionsChanged) {
      lastAppliedFilterConditionsKeyRef.current = filterConditionsKey;
    }

    runHydrationAction(action);
    setIsFilterReady(true);
  }, [
    accountList,
    accountListKey,
    applyHydratedFilter,
    filterConditionsKey,
    isUserLoaded,
    syncToServer,
    user.contactId,
    user.filterConditions,
  ]);

  const filteredAccountList = useMemo(
    () => filterAccountsByDepartment(accountList, chosenDepartment),
    [accountList, chosenDepartment]
  );

  const persistFilter = useCallback(
    (department: string | undefined, account: Account | undefined) => {
      saveGlobalFilterToLocalStorage(department, account);
      void syncToServer(department, account);
    },
    [syncToServer]
  );

  const setChosenDepartment = useCallback(
    (department: string) => {
      if (department === INIT_DEPARTMENT || department === "") {
        if (isSameDepartment(chosenDepartmentRef.current, undefined)) {
          return;
        }
        chosenDepartmentRef.current = undefined;
        setChosenDepartmentState(undefined);
        persistFilter(undefined, chosenAccountRef.current);
        return;
      }

      const nextAccount =
        chosenAccountRef.current && chosenAccountRef.current.department !== department
          ? undefined
          : chosenAccountRef.current;

      if (
        isSameDepartment(chosenDepartmentRef.current, department) &&
        isSameAccount(chosenAccountRef.current, nextAccount)
      ) {
        return;
      }

      chosenDepartmentRef.current = department;
      setChosenDepartmentState(department);
      if (!isSameAccount(chosenAccountRef.current, nextAccount)) {
        chosenAccountRef.current = nextAccount;
        setChosenAccountState(nextAccount);
      }
      persistFilter(department, nextAccount);
    },
    [persistFilter]
  );

  const setChosenAccount = useCallback(
    (account: Account) => {
      if (account.id === INIT_ACCOUNT.id) {
        if (isSameAccount(chosenAccountRef.current, undefined)) {
          return;
        }
        chosenAccountRef.current = undefined;
        setChosenAccountState(undefined);
        persistFilter(chosenDepartmentRef.current, undefined);
        return;
      }

      if (isSameAccount(chosenAccountRef.current, account)) {
        return;
      }

      chosenAccountRef.current = account;
      setChosenAccountState(account);
      persistFilter(chosenDepartmentRef.current, account);
    },
    [persistFilter]
  );

  return {
    chosenDepartment,
    chosenAccount,
    setChosenDepartment,
    setChosenAccount,
    filteredAccountList,
    isFilterReady,
  };
};
